/**
 * yoyoIR1Protocol.js — Yo-Yo Intermittent Recovery Test, Level 1 (Yo-Yo IR1).
 * ============================================================================
 *
 * ⚠️ AUDITABLE PROTOCOL DATA — VERIFY BEFORE COMPETITIVE/RESEARCH USE.
 *
 * This file is the SINGLE SOURCE OF TRUTH for Yo-Yo IR1 timing. The audio
 * runner does NOT contain any timing constants — it only plays what this
 * protocol describes. To correct the test, edit ONLY this table.
 *
 * Structure (standard Yo-Yo IR1, Bangsbo):
 *   • Each "shuttle" = 2 × 20 m  (20 m out + 20 m back = 40 m).
 *   • After every 40 m shuttle there is a 10 s ACTIVE RECOVERY (2 × 5 m jog).
 *   • Speed increases in steps; the number of shuttles run at each speed is
 *     fixed by the protocol (1, 1, 2, 3, 4, then 8 per step).
 *   • The announced/displayed "level" follows the original Yo-Yo numbering
 *     (5, 9, 11, 12, 13, 14 …) which is why it is not 1,2,3…
 *   • RESULT = total running distance (m) = completed 40 m shuttles × 40.
 *
 * shuttleDuration is DERIVED from speed (time to cover 40 m at that speed):
 *     shuttleDuration = 40 / (speed_kmh / 3.6) = 144 / speed_kmh   [seconds]
 *
 * SOURCES TO VERIFY AGAINST (mark each value as confirmed in QA view):
 *   • Bangsbo J, Iaia FM, Krustrup P (2008). "The Yo-Yo intermittent recovery
 *     test." Sports Medicine 38(1):37-51.
 *   • Krustrup P et al. (2003), Med Sci Sports Exerc.
 *   • Public protocol tables (Topend Sports / Science for Sport).
 *
 * verified=false → the QA view will display a "verify against official table"
 * banner. Set to true only after a physical-coach sign-off.
 */

const DISTANCE_PER_SHUTTLE = 40; // metres (2 × 20 m)
const RECOVERY = 10; // seconds active recovery after each shuttle

// [displayedLevel, speed (km/h), shuttles at this speed]
const SPEED_TABLE = [
  [5, 10.0, 1],
  [9, 12.0, 1],
  [11, 13.0, 2],
  [12, 13.5, 3],
  [13, 14.0, 4],
  [14, 14.5, 8],
  [15, 15.0, 8],
  [16, 15.5, 8],
  [17, 16.0, 8],
  [18, 16.5, 8],
  [19, 17.0, 8],
  [20, 17.5, 8],
  [21, 18.0, 8],
  [22, 18.5, 8],
  [23, 19.0, 8],
];

function buildLevels() {
  return SPEED_TABLE.map(([level, speed, shuttles]) => ({
    level,
    speed,
    shuttles,
    // Time to run one 40 m shuttle at this speed (seconds). NOT rounded — the
    // audio scheduler needs full precision to avoid drift.
    shuttleDuration: DISTANCE_PER_SHUTTLE / (speed / 3.6),
    recoveryDuration: RECOVERY,
  }));
}

export const yoyoIR1Protocol = {
  testId: 'yoyo_ir1',
  variantId: 'yoyo_ir1_bangsbo_2008_default',
  name: 'Yo-Yo IR1',
  displayName: 'Yo-Yo Intermittent Recovery Test Level 1',
  shortLabel: 'Yo-Yo IR1 / IRTL1',
  spokenName: 'Yo-Yo Intermittent Recovery Test Level One',
  distancePerShuttle: DISTANCE_PER_SHUTTLE,
  outAndBack: true, // 20 m out + 20 m back → turn beep at 20 m, finish beep at 40 m
  hasRecovery: true,
  recoverySeconds: RECOVERY,
  startStage: '5.1',
  startSpeed: 10.0, // km/h at stage 5.1
  expectedTotalShuttles: 91,
  expectedMaxDistance: 3640, // m (91 × 40)
  scoring: 'Senast fullbordade 40 m-shuttle (genomförda 20 m ut + 20 m tillbaka).',
  resultUnit: 'm',
  protocolVersion: 'trainnova-yoyo-ir1-91-v2',
  verified: false,
  source:
    'Bangsbo, Iaia & Krustrup (2008), Sports Medicine 38(1). Speed/shuttle table per standard Yo-Yo IR1.',
  notes:
    'Shuttle = 2×20 m, 10 s aktiv återhämtning mellan varje shuttle. Resultat = genomförda 40 m-sträckor × 40 m. Hastighetstabellen MÅSTE verifieras mot officiell Yo-Yo IR1-tabell innan skarp användning.',
  levels: buildLevels(),
};

export default yoyoIR1Protocol;
